/**
 * Turns a stored version back into applicable `asset_metadata` — normalising it so a shallow
 * merge cannot leave stale state behind, and rebuilding captions when they were stripped at
 * save time.
 */

import type { ClipVersionDetail } from '@goldcast/api/content';
import { Clip, ClipMetadata } from '@goldcast/player-config/src/types/clip-types';
import { isSplitsInTimelineEnabledPure } from '@goldcast/player-config/src/utils/clipUtils';
import { updateCaptions as updateCaptionsNonSplit } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtils/captionUtils';
import {
  updateCaptions as updateCaptionsSplit,
  updateGlobalCaptions
} from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtilsWithSplits/captionUtils';
import appLogger from '@/services/logger';
import { currentClip } from '@/stores/clip';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';

interface PlayerConfigSegment {
  segment_metadata?: Record<string, unknown>;
}

interface PlayerConfigTrack {
  segments?: PlayerConfigSegment[];
}

const EDIT_STATE_DEFAULTS = {
  edits: () => ({}),
  deletes: () => ({}),
  merges: () => [] as unknown[]
} as const;

function applyEditStateDefaults(target: Record<string, unknown>): void {
  Object.entries(EDIT_STATE_DEFAULTS).forEach(([key, makeDefault]) => {
    if (target[key] === undefined || target[key] === null) {
      target[key] = makeDefault();
    }
  });
}

/**
 * Facts about the recording rather than the user's edits, so a restore must leave them alone.
 *
 * A stored snapshot can omit them, and clearing `original_lang` in particular breaks the
 * transcript: `isClipInOriginalLanguage` treats a clip with `selected_lang` set and
 * `original_lang` undefined as translated, which points the transcript at the version's
 * captions — stripped to `[]` on any captions-recomputable version — and empties the panel.
 */
const PRESERVED_KEYS = new Set(['original_lang', 'translated_langs']);

/**
 * Makes every key the live clip carries explicitly present on the restored metadata, so the
 * shallow merge in updateClipMetadataWithId cannot leave a stale value behind.
 *
 * A version stores a full clone of asset_metadata, so a key missing from it was `undefined`
 * when that version was saved — writing `undefined` back reproduces the saved state exactly.
 * Absent keys are otherwise skipped by the spread and the current clip's value survives,
 * which is what left an intro/outro (and any other optional field: magicLayout, template_id,
 * caption_positions…) applied after restoring a version that never had one.
 */
function clearKeysAbsentFromVersion(live: ClipMetadata, meta: ClipMetadata): void {
  const target = meta as unknown as Record<string, unknown>;
  Object.keys(live).forEach(key => {
    if (!(key in target) && !PRESERVED_KEYS.has(key)) {
      target[key] = undefined;
    }
  });
}

function normalizeRestoredMetadata(live: ClipMetadata, meta: ClipMetadata): void {
  clearKeysAbsentFromVersion(live, meta);

  // Runs after the clearing pass: edits/deletes/merges are declared non-optional, so they
  // need an empty container rather than the `undefined` that pass would leave.
  applyEditStateDefaults(meta as unknown as Record<string, unknown>);

  // Per-segment edit-state lives under segment_metadata on composite segments.
  const tracks = (meta.player_config as { playerConfig?: { tracks?: PlayerConfigTrack[] } } | undefined)?.playerConfig
    ?.tracks;
  if (!Array.isArray(tracks)) {
    return;
  }
  tracks.forEach(track => {
    if (!Array.isArray(track?.segments)) {
      return;
    }
    track.segments.forEach(segment => {
      if (segment?.segment_metadata && typeof segment.segment_metadata === 'object') {
        applyEditStateDefaults(segment.segment_metadata);
      }
    });
  });
}

/**
 * Builds applicable asset_metadata from a stored version:
 *   1. Deep-clones it — stored objects are frozen and the caption rebuild mutates in place
 *   2. Normalizes it so the shallow merge in updateClipMetadataWithId fully overwrites
 *   3. Rebuilds captions from the live transcript when the version was stored without them
 *
 * Captions are only rebuilt for `captions_recomputable` versions. A translated version's
 * captions come from a backend job, cannot be derived on the client, and are stored verbatim.
 *
 * Returns null if the clip is not loaded or the version carries no metadata.
 */
export function buildRestoredMetadata(clipId: string, detail: ClipVersionDetail): ClipMetadata | null {
  const clip = currentClip.getSnapshot()[clipId];
  if (!clip || !detail.asset_metadata) {
    return null;
  }

  const meta = structuredClone(detail.asset_metadata) as ClipMetadata;
  normalizeRestoredMetadata(clip.asset_metadata, meta);

  const restoredClip: Clip = { ...clip, asset_metadata: meta };

  if (detail.captions_recomputable !== false) {
    try {
      const isSplits = isSplitsInTimelineEnabledPure({
        clip: restoredClip,
        isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
      });
      if (isSplits) {
        updateCaptionsSplit(restoredClip);
        updateGlobalCaptions(restoredClip);
      } else {
        updateCaptionsNonSplit(restoredClip);
      }
    } catch (err) {
      // Restore the rest of the version rather than failing outright — the clip is
      // recoverable with stale captions, and a language switch rebuilds them.
      appLogger.error('[versioning] caption recompute failed on restore', err);
    }
  }

  return restoredClip.asset_metadata;
}
